import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { TelemetryPage, parsePath } from '@/types/routes';

interface RouterContextType {
  view: TelemetryPage;
  path: string;
  params: Record<string, string>;
  navigate: (path: string, replace?: boolean) => void;
}

const RouterContext = createContext<RouterContextType>({
  view: TelemetryPage.Dashboard,
  path: '',
  params: {},
  navigate: () => {},
});

export function RouterProvider({ children }: { children: ReactNode }) {
  const getState = useCallback(() => {
    let hash = window.location.hash;
    if (!hash || hash === '#' || hash === '#/') {
      const pathname = window.location.pathname.replace(/\/+$/, '');
      if (pathname && pathname !== '' && pathname !== '/index.html') {
        hash = `#${pathname}${window.location.search || ''}`;
      } else {
        hash = hash || '#/';
      }
    }
    const parsed = parsePath(hash);
    return { view: parsed.page, path: hash, params: parsed.params };
  }, []);

  const [state, setState] = useState(getState);

  const navigate = useCallback((path: string, replace = false) => {
    let hashPath = path;
    if (hashPath.startsWith('#/')) {
      // already in correct format #/...
    } else if (hashPath.startsWith('#')) {
      hashPath = `#/${hashPath.slice(1)}`;
    } else if (hashPath.startsWith('/')) {
      hashPath = `#${hashPath}`;
    } else {
      hashPath = `#/${hashPath}`;
    }
    const historyUrl =
      window.location.pathname !== '/' && window.location.pathname !== '/index.html'
        ? `/${hashPath}`
        : hashPath;
    if (replace) {
      window.history.replaceState(null, '', historyUrl);
    } else {
      window.history.pushState(null, '', historyUrl);
    }
    const parsed = parsePath(hashPath);
    setState({ view: parsed.page, path: hashPath, params: parsed.params });
  }, []);

  useEffect(() => {
    const onHashChange = () => setState(getState());
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('popstate', onHashChange);
    };
  }, [getState]);

  return (
    <RouterContext.Provider value={{ ...state, navigate }}>
      {children}
    </RouterContext.Provider>
  );
}

export const useRouter = () => useContext(RouterContext);
